import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Line } from "react-chartjs-2";
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Row, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { statusLabel, statusColor, surfaceLabel, METRIC_LABELS, CONTROL_METRIC, metricTerms } from "../../helpers/experiments";
import {
    sortedRows,
    latestRow,
    seriesFromRows,
    formatVerdict,
    daysRemaining,
    daysElapsed,
    revenueOf,
    segmentsByDimension,
    dimensionLabel,
    hasSRMAlert,
    fmtPct,
    fmtEurPrecise,
    fmtNumber,
    fmtEur,
    MIN_WIN_PROBABILITY,
} from "../../helpers/experimentResults";

// Palette : contrôle en gris, variantes en couleur. Deux variantes suffisent
// aujourd'hui, la liste boucle si le registre en accepte plus un jour.
const VARIANT_COLORS = ["#6c757d", "#3f80ea", "#d9534f", "#f0ad4e"];

// La commission par visiteur est un PLANCHER, jamais un montant exact : une
// partie des ventes n'a pas d'identifiant de clic (inattribuables) et les
// visiteurs des derniers jours n'ont pas encore parcouru leur fenêtre de 7
// jours. Le chiffre ne peut donc que monter — il ne sert qu'à repérer une
// variante qui casserait le revenu, jamais à décider.
const REVENUE_FLOOR_CAVEAT =
    "Ventes sans identifiant de clic non attribuables ; visiteurs récents pas encore à 7 jours.";

const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime()) || date.getFullYear() < 1971) return "—";
    return date.toLocaleDateString("fr-FR");
};

// Page de résultats d'une expérience : ce qui est mesuré, ce que ça vaut, et
// ce que les garde-fous en disent. Le revenu est affiché comme CONTRÔLE (il ne
// tranche jamais), les segments sont des compteurs (aucun test statistique).
export function ExperimentResults(props) {
    const id = props.match && props.match.params ? props.match.params.id : "";

    const [experiment, setExperiment] = useState(null);
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    // Date d'analyse affichée : "" = la plus récente. Chaque ligne d'experiment_results
    // est le CUMUL depuis le démarrage jusqu'à cette date (demande PM 05/10/2026).
    const [selectedDate, setSelectedDate] = useState("");

    useEffect(() => {
        if (!id) return;
        let cancelled = false;

        apiFetch(API.back + "/experiments", { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                setExperiment(list.find((e) => e.id === id) || null);
            })
            .catch((err) => {
                console.log(err);
                if (!cancelled) setError("Expérience introuvable (" + err.message + ").");
            });

        apiFetch(API.back + "/experiment-results?id=" + encodeURIComponent(id), { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                if (!cancelled) setRows(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.log(err);
                if (!cancelled) {
                    setRows([]);
                    setError("Résultats indisponibles (" + err.message + ").");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [id]);

    const availableDates = (rows || []).map((r) => r.date).filter(Boolean).sort().reverse();
    const row = (selectedDate && (rows || []).find((r) => r.date === selectedDate)) || latestRow(rows);
    const verdict = formatVerdict(row);
    const series = seriesFromRows(rows);
    const revenue = revenueOf(row);
    const segments = segmentsByDimension(row);
    const remaining = daysRemaining(experiment);
    const elapsed = daysElapsed(experiment);
    const minDays = experiment && experiment.rules ? Number(experiment.rules.minDays) || 0 : 0;
    const minExposures = experiment && experiment.rules ? Number(experiment.rules.minExposures) || 0 : 0;
    const terms = metricTerms(experiment);

    const chartData = {
        labels: series.labels,
        datasets: series.keys.map((key, index) => ({
            label: "Exposés " + key,
            fill: false,
            spanGaps: true,
            backgroundColor: "transparent",
            borderColor: VARIANT_COLORS[index % VARIANT_COLORS.length],
            data: series.exposed[key],
            yAxisID: "exposed",
        })).concat(
            series.keys.map((key, index) => ({
                label: "Taux " + key,
                fill: false,
                spanGaps: true,
                backgroundColor: "transparent",
                borderColor: VARIANT_COLORS[index % VARIANT_COLORS.length],
                borderDash: [4, 4],
                data: series.rate[key],
                yAxisID: "rate",
            }))
        ),
    };

    const chartOptions = {
        maintainAspectRatio: false,
        legend: { display: true },
        tooltips: { intersect: false },
        scales: {
            xAxes: [{ gridLines: { color: "rgba(0,0,0,0.05)" } }],
            yAxes: [
                { id: "exposed", position: "left", scaleLabel: { display: true, labelString: "Exposés (cumul)" } },
                {
                    id: "rate",
                    position: "right",
                    gridLines: { display: false },
                    scaleLabel: { display: true, labelString: terms.rate },
                    ticks: { callback: (value) => (value * 100).toFixed(1) + " %" },
                },
            ],
        },
    };

    return (
        <div>
            <Card className="flex-fill w-100">
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <CardTitle tag="h5" className="mb-1">
                                {experiment ? experiment.name : "Résultats de l'expérience"}
                            </CardTitle>
                            {experiment ? (
                                <div className="text-muted small">
                                    {surfaceLabel(experiment.surface)} —{" "}
                                    <Badge color={statusColor(experiment.status)}>{statusLabel(experiment.status)}</Badge>
                                    {experiment.winnerApplied ? (
                                        <Badge color="success" className="ml-1">
                                            gagnant appliqué à la surface
                                        </Badge>
                                    ) : null}
                                    <div className="mt-1">
                                        Démarrée le {fmtDate(experiment.startedAt)}
                                        {experiment.endedAt ? " — terminée le " + fmtDate(experiment.endedAt) : ""}
                                    </div>
                                    <div>
                                        Métrique : {METRIC_LABELS[experiment.metric && experiment.metric.primary] || "—"} —
                                        contrôle : {METRIC_LABELS[CONTROL_METRIC]}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <Link to="/experiments/list" className="btn btn-outline-secondary btn-sm">
                            Retour à la liste
                        </Link>
                    </div>
                </CardHeader>
                <CardBody>
                    {error ? <div className="alert alert-danger">{error}</div> : null}
                    {experiment && experiment.hypothesis ? (
                        <div className="alert alert-light border">
                            <b>Hypothèse :</b> {experiment.hypothesis}
                        </div>
                    ) : null}

                    {availableDates.length > 0 ? (
                        <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
                            <label htmlFor="exp-date" className="mb-0"><b>Résultats au</b></label>
                            <input
                                id="exp-date"
                                type="date"
                                className="form-control form-control-sm"
                                style={{ width: 170 }}
                                value={row ? row.date : ""}
                                min={availableDates[availableDates.length - 1]}
                                max={availableDates[0]}
                                onChange={(e) => {
                                    const d = e.target.value;
                                    // Pas d'analyse ce jour-là : on prend la plus proche avant.
                                    const pick = availableDates.find((x) => x <= d) || availableDates[availableDates.length - 1];
                                    setSelectedDate(pick === availableDates[0] ? "" : pick);
                                }}
                            />
                            {selectedDate ? (
                                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setSelectedDate("")}>
                                    revenir au plus récent
                                </button>
                            ) : (
                                <span className="text-muted small">analyse la plus récente</span>
                            )}
                            <span className="text-muted small">— cumul depuis le démarrage du test jusqu'à cette date</span>
                        </div>
                    ) : null}

                    <div className={"alert alert-" + verdict.tone}>
                        <b>{verdict.label}</b>
                        {verdict.reason ? <div className="small mt-1">{verdict.reason}</div> : null}
                        {row ? (
                            <div className="small mt-1">
                                Analyse du {row.date} — {fmtNumber(row.daysRunning)} jour(s) de test. Un gagnant n'est proposé
                                qu'au-delà de {fmtPct(MIN_WIN_PROBABILITY, 1)} de probabilité.
                            </div>
                        ) : null}
                    </div>

                    {/* Garde-fous visibles : ce qui empêche (ou autorise) de décider. */}
                    <Row className="mb-2">
                        <Col md={4}>
                            <div className="text-muted small">Durée</div>
                            <div>
                                {elapsed === null
                                    ? "Non démarrée"
                                    : elapsed + " jour(s) écoulé(s) — minimum " + minDays + " j"}
                            </div>
                            {remaining !== null ? (
                                <div className={remaining < 0 ? "text-danger small" : "text-muted small"}>
                                    {remaining < 0
                                        ? "Durée maximale dépassée de " + Math.abs(remaining) + " jour(s) : décider."
                                        : "Il reste " + remaining + " jour(s) avant la durée maximale."}
                                </div>
                            ) : null}
                            {elapsed !== null && elapsed < minDays ? (
                                <div className="text-warning small">Durée minimale non atteinte : ne pas conclure.</div>
                            ) : null}
                        </Col>
                        <Col md={4}>
                            <div className="text-muted small">Expositions minimales par variante</div>
                            <div>{fmtNumber(minExposures)}</div>
                            {row && Array.isArray(row.variants) && row.variants.some((v) => (v.exposed || 0) < minExposures) ? (
                                <div className="text-warning small">Seuil non atteint sur au moins une variante.</div>
                            ) : null}
                        </Col>
                        <Col md={4}>
                            <div className="text-muted small">Répartition (SRM)</div>
                            <div>
                                χ² {row ? fmtNumber(Number(row.srmChi2).toFixed(2)) : "—"} — p{" "}
                                {row && row.srmP !== undefined ? Number(row.srmP).toFixed(4).replace(".", ",") : "—"}
                            </div>
                            {hasSRMAlert(row) ? (
                                <div className="text-danger small">
                                    Répartition anormale : les effectifs s'écartent des poids déclarés. Ne pas décider sur ces
                                    chiffres tant que la cause n'est pas comprise.
                                </div>
                            ) : null}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {!rows && !error ? (
                <Card>
                    <CardBody>
                        <div className="d-flex align-items-center">
                            <Spinner color="primary" size="sm" />
                            <span className="text-muted ml-2">Chargement des résultats…</span>
                        </div>
                    </CardBody>
                </Card>
            ) : null}

            {rows && rows.length === 0 ? (
                <Card>
                    <CardBody>
                        <p className="text-muted mb-0">
                            Aucune ligne d'analyse pour le moment. L'analyseur écrit une ligne par jour une fois l'expérience
                            démarrée.
                        </p>
                    </CardBody>
                </Card>
            ) : null}

            {row ? (
                <Row>
                    {(row.variants || []).map((variant, index) => (
                        <Col md={6} key={variant.key}>
                            <Card>
                                <CardHeader>
                                    <CardTitle tag="h5" className="mb-0">
                                        Variante {variant.key}
                                        {index === 0 ? " (contrôle)" : ""}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Table size="sm" className="mb-0">
                                        <tbody>
                                            <tr>
                                                <td>Visiteurs exposés</td>
                                                <td className="text-right">{fmtNumber(variant.exposed)}</td>
                                            </tr>
                                            <tr>
                                                <td>{terms.count}</td>
                                                <td className="text-right">{fmtNumber(variant.converted)}</td>
                                            </tr>
                                            <tr>
                                                <td>{terms.rate}</td>
                                                <td className="text-right">
                                                    <b>{fmtPct(variant.rate)}</b>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Acheteurs identifiés
                                                    <div className="text-muted small">ventes rattachées à un visiteur connecté, 7 j après l'exposition</div>
                                                </td>
                                                <td className="text-right">{revenue && revenue.buyers[variant.key] !== undefined ? fmtNumber(revenue.buyers[variant.key]) : "—"}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Taux de conversion (ventes réelles){" "}
                                                    <Badge color="secondary" title={REVENUE_FLOOR_CAVEAT}>
                                                        plancher
                                                    </Badge>
                                                    <div className="text-muted small">acheteurs identifiés ÷ visiteurs exposés — contrôle, jamais décisionnel</div>
                                                </td>
                                                <td className="text-right">
                                                    <b>{revenue && revenue.conversion ? fmtPct(revenue.conversion[variant.key]) : "—"}</b>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Ventes</td>
                                                <td className="text-right">{revenue && revenue.sales[variant.key] !== undefined ? fmtNumber(revenue.sales[variant.key]) : "—"}</td>
                                            </tr>
                                            <tr>
                                                <td>Commission totale</td>
                                                <td className="text-right">{revenue && revenue.totalEUR[variant.key] !== undefined ? fmtEur(revenue.totalEUR[variant.key]) : "—"}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Commission / visiteur exposé{" "}
                                                    <Badge color="secondary" title={REVENUE_FLOOR_CAVEAT}>
                                                        plancher
                                                    </Badge>
                                                    <div className="text-muted small">contrôle, jamais décisionnel</div>
                                                    <div className="text-muted small">{REVENUE_FLOOR_CAVEAT}</div>
                                                </td>
                                                <td className="text-right">
                                                    {revenue ? fmtEurPrecise(revenue.variantsEUR[variant.key]) : "—"}
                                                </td>
                                            </tr>
                                            {index > 0 ? (
                                                <React.Fragment>
                                                    <tr>
                                                        <td>P(B &gt; A)</td>
                                                        <td className="text-right">
                                                            <b>{fmtPct(row.probBBeatsA, 3)}</b>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Gain relatif (IC 90 %)</td>
                                                        <td className="text-right">
                                                            {Array.isArray(row.liftCI90)
                                                                ? fmtPct(row.liftCI90[0]) + " → " + fmtPct(row.liftCI90[1])
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            Perte attendue si l'on choisit {variant.key}
                                                            <div className="text-muted small">
                                                                en points de {terms.rate.toLowerCase()}
                                                            </div>
                                                        </td>
                                                        <td className="text-right">{fmtPct(row.expectedLoss, 4)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>P(B &gt; A) sur les ventes réelles</td>
                                                        <td className="text-right">
                                                            {revenue && revenue.conversionProbBBeatsA !== null
                                                                ? fmtPct(revenue.conversionProbBBeatsA, 1)
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Gain relatif sur les ventes (IC 90 %)</td>
                                                        <td className="text-right">
                                                            {revenue && revenue.conversionLiftCI90
                                                                ? fmtPct(revenue.conversionLiftCI90[0]) + " → " + fmtPct(revenue.conversionLiftCI90[1])
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>P(B &gt; A) sur le revenu</td>
                                                        <td className="text-right">
                                                            {revenue && revenue.probBBeatsA !== null && revenue.probBBeatsA !== undefined
                                                                ? fmtPct(revenue.probBBeatsA, 1)
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ) : (
                                                <tr>
                                                    <td>Perte attendue si l'on garde A</td>
                                                    <td className="text-right">{fmtPct(row.expectedLossA, 4)}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : null}

            {series.labels.length > 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle tag="h5" className="mb-0">
                            Jour par jour (cumuls)
                        </CardTitle>
                        <div className="text-muted small">
                            Trait plein : visiteurs exposés. Pointillés : {terms.rate.toLowerCase()}. Chaque point est une analyse
                            quotidienne, cumulée depuis le démarrage.
                        </div>
                    </CardHeader>
                    <CardBody>
                        <div style={{ height: 320 }}>
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    </CardBody>
                </Card>
            ) : null}

            {segments.length > 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle tag="h5" className="mb-0">
                            Ventilation par segment
                        </CardTitle>
                        <div className="text-muted small">
                            Compteurs seulement : aucun test statistique par segment (les effectifs sont trop faibles pour
                            décider segment par segment). À lire comme une piste, pas comme un verdict.
                        </div>
                    </CardHeader>
                    <CardBody>
                        {segments.map((group) => (
                            <div key={group.dimension} className="mb-3">
                                <h6>{dimensionLabel(group.dimension)}</h6>
                                <div style={{ overflowX: "auto" }}>
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>{dimensionLabel(group.dimension)}</th>
                                                {series.keys.map((key) => (
                                                    <th key={key} className="text-right">
                                                        {key} — exposés / conv. / taux
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.rows.map((segment) => (
                                                <tr key={segment.value}>
                                                    <td>{segment.value}</td>
                                                    {series.keys.map((key) => {
                                                        const variant = segment.variants.find((v) => v.key === key);
                                                        if (!variant) {
                                                            return (
                                                                <td key={key} className="text-right">
                                                                    —
                                                                </td>
                                                            );
                                                        }
                                                        const rate = variant.exposed > 0 ? variant.converted / variant.exposed : null;
                                                        return (
                                                            <td key={key} className="text-right">
                                                                {fmtNumber(variant.exposed)} / {fmtNumber(variant.converted)} /{" "}
                                                                {fmtPct(rate)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            ) : null}

            {rows && rows.length > 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle tag="h5" className="mb-0">
                            Historique des analyses
                        </CardTitle>
                    </CardHeader>
                    <CardBody>
                        <div style={{ overflowX: "auto" }}>
                            <Table size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Jour</th>
                                        {series.keys.map((key) => (
                                            <th key={key} className="text-right">
                                                {key} — exposés / taux
                                            </th>
                                        ))}
                                        <th className="text-right">P(B&gt;A)</th>
                                        {series.keys.map((key) => (
                                            <th key={"conv-" + key} className="text-right">
                                                {key} — conv. ventes
                                            </th>
                                        ))}
                                        <th className="text-right">P(B&gt;A) ventes</th>
                                        <th>Verdict</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows(rows)
                                        .slice()
                                        .reverse()
                                        .map((line) => (
                                            <tr key={line.date}>
                                                <td>{line.date}</td>
                                                {series.keys.map((key) => {
                                                    const variant = (line.variants || []).find((v) => v.key === key);
                                                    return (
                                                        <td key={key} className="text-right">
                                                            {variant ? fmtNumber(variant.exposed) + " / " + fmtPct(variant.rate) : "—"}
                                                        </td>
                                                    );
                                                })}
                                                <td className="text-right">{fmtPct(line.probBBeatsA, 3)}</td>
                                                {series.keys.map((key) => {
                                                    const lineRevenue = revenueOf(line);
                                                    return (
                                                        <td key={"conv-" + key} className="text-right">
                                                            {lineRevenue && lineRevenue.conversion ? fmtPct(lineRevenue.conversion[key]) : "—"}
                                                        </td>
                                                    );
                                                })}
                                                <td className="text-right">
                                                    {(() => {
                                                        const lineRevenue = revenueOf(line);
                                                        return lineRevenue && lineRevenue.conversionProbBBeatsA !== null
                                                            ? fmtPct(lineRevenue.conversionProbBBeatsA, 1)
                                                            : "—";
                                                    })()}
                                                </td>
                                                <td>{formatVerdict(line).label}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            ) : null}
        </div>
    );
}

export default ExperimentResults;
